"use client";

/**
 * Notificaciones 5.4 — lista de avisos + preferencias (locales).
 */

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";

interface Notificacion {
  id: number;
  texto: string;
  leida: boolean;
  creado: string;
}

const PREFS = [
  { clave: "pedidos", label: "Estado de mis pedidos", porDefecto: true },
  { clave: "plan", label: "Recordatorio de mi plan", porDefecto: true },
  { clave: "promos", label: "Promos y novedades", porDefecto: false },
];

function hace(iso: string): string {
  const f = new Date(`${iso.replace(" ", "T")}Z`).getTime();
  const min = Math.floor((Date.now() - f) / 60000);
  if (min < 60) return `Hace ${Math.max(1, min)} min`;
  if (min < 1440) return `Hace ${Math.floor(min / 60)} h`;
  return new Date(f).toLocaleDateString("es-EC", { day: "numeric", month: "short" });
}

export default function PaginaNotificaciones() {
  const router = useRouter();
  const { usuario } = useApp();
  const [avisos, setAvisos] = useState<Notificacion[] | null>(null);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (usuario === null) router.replace("/acceso?next=/cuenta/notificaciones");
  }, [usuario, router]);

  useEffect(() => {
    try {
      const crudo = localStorage.getItem("fz_notif_prefs");
      setPrefs(
        crudo
          ? JSON.parse(crudo)
          : Object.fromEntries(PREFS.map((p) => [p.clave, p.porDefecto])),
      );
    } catch {}
  }, []);

  const usuarioId = usuario?.id;
  useEffect(() => {
    if (!usuarioId) return;
    fetch("/api/cuenta/notificaciones")
      .then((r) => r.json())
      .then((data) => {
        setAvisos(data.ok ? data.notificaciones : []);
        // Al abrir la pantalla se marcan como leídas.
        void fetch("/api/cuenta/notificaciones", { method: "POST" });
      })
      .catch(() => setAvisos([]));
  }, [usuarioId]);

  function togglePref(clave: string) {
    setPrefs((p) => {
      const nuevo = { ...p, [clave]: !p[clave] };
      try {
        localStorage.setItem("fz_notif_prefs", JSON.stringify(nuevo));
      } catch {}
      return nuevo;
    });
  }

  return (
    <>
      <HeaderTitulo titulo="Notificaciones" href="/cuenta" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[62px]">
        <div className="flex flex-col gap-3 pt-[22px] pb-[30px]">
          {avisos === null ? (
            <p className="pt-8 text-center text-sm text-tinta/50">Cargando…</p>
          ) : avisos.length === 0 ? (
            <p className="rounded-[22px] bg-humo p-4 text-sm leading-5 text-tinta/65">
              Nada por aquí todavía. Te avisamos cuando tu pedido avance.
            </p>
          ) : (
            avisos.map((n) => (
              <div
                key={n.id}
                className="flex gap-3.5 rounded-[22px] border border-black/5 bg-white p-4"
              >
                <span
                  className={`mt-1.5 h-2.5 w-2.5 flex-none rounded-full ${n.leida ? "bg-humo" : "bg-lima"}`}
                />
                <div>
                  <div className="font-app text-[15px] font-semibold">{n.texto}</div>
                  <div className="mt-1 text-xs text-tinta/50">{hace(n.creado)}</div>
                </div>
              </div>
            ))
          )}

          <h2 className="mt-3 font-app text-base font-semibold">Preferencias</h2>
          <div className="overflow-hidden rounded-3xl border border-black/5 bg-white">
            {PREFS.map((p) => {
              const activo = prefs[p.clave] ?? p.porDefecto;
              return (
                <div
                  key={p.clave}
                  className="flex items-center justify-between border-b border-black/5 px-5 py-4 last:border-b-0"
                >
                  <span className="text-[15px]">{p.label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={activo}
                    aria-label={p.label}
                    onClick={() => togglePref(p.clave)}
                    className={`relative h-7 w-[46px] rounded-[14px] transition-colors ${activo ? "bg-verde" : "bg-[#d9d9d9]"}`}
                  >
                    <span
                      className={`absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white transition-all ${activo ? "left-[22px]" : "left-[3px]"}`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
